import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import DiamondIcon from '@mui/icons-material/Diamond';
import BuildIcon from '@mui/icons-material/Build';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

const PageTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '3rem',
  color: '#1a1a1a',
  marginBottom: '2rem',
  textAlign: 'center',
});

const ServiceCard = styled(Paper)({
  padding: '2rem',
  height: '100%',
  backgroundColor: '#fff',
  boxShadow: 'none',
  border: '1px solid #eee',
  borderRadius: '8px',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  },
});

const ServiceTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '2rem',
  color: '#1a1a1a',
  marginBottom: '1rem',
  textAlign: 'center',
});

const ServiceDescription = styled(Typography)({
  fontSize: '1.1rem',
  lineHeight: '1.8',
  color: '#333',
  marginBottom: '1.5rem',
  textAlign: 'center',
});

const IconWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '1.5rem',
  '& .MuiSvgIcon-root': {
    fontSize: '3rem',
    color: '#c4a661',
  },
});

const CustomButton = styled(Button)({
  backgroundColor: '#1a1a1a',
  color: '#fff',
  padding: '1rem 2rem',
  marginTop: '1rem',
  '&:hover': {
    backgroundColor: '#c4a661',
  },
});

const services = [
  {
    title: 'Professional Cleaning',
    description: 'Restore your jewelry\'s brilliance with our expert cleaning service. We use state-of-the-art techniques to remove dirt, oils, and residue, bringing back the original sparkle to your precious pieces.',
    icon: DiamondIcon,
  },
  {
    title: 'Jewelry Repairs',
    description: 'From stone setting to chain repairs, our skilled craftsmen can restore your beloved pieces to their former glory. We handle all types of repairs with precision and care, ensuring your jewelry lasts for generations.',
    icon: BuildIcon,
  },
  {
    title: 'Polish & Refinishing',
    description: 'Give your jewelry a renewed shine with our professional polishing service. We carefully restore the surface finish of your pieces, removing scratches and wear marks while preserving the integrity of your jewelry.',
    icon: AutoFixHighIcon,
  },
];

const ServicesPage = () => {
  const navigate = useNavigate();

  return (
    <PageLayout>
      <Container>
        <Box sx={{ py: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <PageTitle>Our Services</PageTitle>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              align="center"
              sx={{ mb: 6, fontSize: '1.2rem' }}
            >
              Expert care for your precious jewelry pieces
            </Typography>

            <Grid container spacing={4}>
              {services.map((service, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                  >
                    <ServiceCard>
                      <IconWrapper>
                        <service.icon />
                      </IconWrapper>
                      <ServiceTitle>{service.title}</ServiceTitle>
                      <ServiceDescription>
                        {service.description}
                      </ServiceDescription>
                    </ServiceCard>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontFamily: 'Cormorant Garamond, serif',
                  color: '#1a1a1a',
                  mb: 3,
                }}
              >
                Need assistance with your jewelry?
              </Typography>
              <CustomButton
                onClick={() => navigate('/contact')}
                variant="contained"
              >
                Contact Us
              </CustomButton>
            </Box>
          </motion.div>
        </Box>
      </Container>
    </PageLayout>
  );
};

export default ServicesPage;
